import { useState, useEffect } from "react";
import { CalendarEvent } from "@/pages/scheduling/types";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { fetchAllGuardSchedules } from "@/pages/scheduling/services/totalWorkService";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useUserRoles } from "@/hooks/useUserRoles";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { useWeekVisibility } from "./useWeekVisibility";
import { startOfWeek, format, addDays, isSunday } from "date-fns";
import { fromLocalISOString } from '@/utils/timezoneUtils';

export const useTotalWorkView = (
  teamId: string,
  guards: any[] | undefined,
  currentDateRange: { start: Date, end: Date },
  refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<CalendarEvent[], Error>>
) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { isAdmin } = useUserRoles();
  const [selectedGuardId, setSelectedGuardId] = useState<string | null>(null);
  const [guardSchedules, setGuardSchedules] = useState<CalendarEvent[]>([]);
  const [allGuardsSchedules, setAllGuardsSchedules] = useState<CalendarEvent[]>([]);
  const [isLoadingGuardSchedules, setIsLoadingGuardSchedules] = useState(false);
  
  const { isDateVisible } = useWeekVisibility(teamId);

  const loadAllGuardsSchedules = async () => {
    if (!guards || guards.length === 0) return;
    
    setIsLoadingGuardSchedules(true);
    try {
      console.log('[TOTAL WORK VIEW] Loading all guards schedules with date range:', {
        start: format(currentDateRange.start, 'yyyy-MM-dd HH:mm:ss'),
        end: format(currentDateRange.end, 'yyyy-MM-dd HH:mm:ss'),
        endDay: format(currentDateRange.end, 'EEEE')
      });
      
      const { start, end } = currentDateRange;
      
      const allSchedules: CalendarEvent[] = [];
      
      if (!isAdmin && user) {
        // For non-admin users, only load their own schedules across all teams
        const guardSchedules = await fetchAllGuardSchedules(user.id, { start, end });
        console.log(`[TOTAL WORK VIEW] Loaded ${guardSchedules.length} schedules for user ${user.id}`);
        allSchedules.push(...guardSchedules);
      } else {
        // For admins, load all guards' schedules across all teams
        const guardsSchedulesPromises = guards.map(guard => fetchAllGuardSchedules(guard.id, { start, end }));
        const guardsSchedulesResults = await Promise.all(guardsSchedulesPromises);
        
        guardsSchedulesResults.forEach((schedules, index) => {
          console.log(`[TOTAL WORK VIEW] Loaded ${schedules.length} schedules for guard ${guards[index].id} (${guards[index].full_name || 'Unknown'})`);
          allSchedules.push(...schedules);
        });
      }
      
      // All schedules now have team_name from the service, no need to warn about missing team names
      
      // Filter schedules based on week visibility and additional logging
      const visibleSchedules = isAdmin ? 
        allSchedules : 
        allSchedules.filter(schedule => {
          const scheduleDate = fromLocalISOString(schedule.start_date);
          const weekStart = startOfWeek(scheduleDate, { weekStartsOn: 1 });
          
          // Check if the schedule falls within the current date range
          // This isn't strictly necessary since fetchAllGuardSchedules now handles this
          // but we keep it for an extra layer of safety
          const inDateRange = scheduleDate >= currentDateRange.start && scheduleDate <= currentDateRange.end;
          
          // Special handling for Sunday, which belongs to the current week
          if (isSunday(scheduleDate)) {
            console.log(`[SUNDAY CHECK] Found Sunday schedule: ${schedule.id} on ${format(scheduleDate, 'yyyy-MM-dd')}`);
          }
          
          const isVisible = isDateVisible(weekStart);
          
          console.log(`[VISIBILITY CHECK] Schedule ${schedule.id} on ${format(scheduleDate, 'yyyy-MM-dd')} - In range: ${inDateRange}, Visible: ${isVisible}`);
          
          return isVisible && inDateRange;
        });
      
      console.log(`[TOTAL WORK VIEW] Total visible schedules: ${visibleSchedules.length}`);
      setAllGuardsSchedules(visibleSchedules);
      setSelectedGuardId(null);
    } catch (error) {
      console.error('[TOTAL WORK VIEW] Error loading all guards schedules:', error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("errorLoadingSchedules"),
      });
    } finally {
      setIsLoadingGuardSchedules(false);
    }
  };

  const loadGuardSchedules = async (guardId: string) => {
    if (!guardId) return;
    
    setIsLoadingGuardSchedules(true);
    try {
      console.log(`[TOTAL WORK VIEW] Loading schedules for guard ${guardId} with date range:`, {
        start: format(currentDateRange.start, 'yyyy-MM-dd HH:mm:ss'),
        end: format(currentDateRange.end, 'yyyy-MM-dd HH:mm:ss'),
        endDay: format(currentDateRange.end, 'EEEE')
      });
      
      const { start, end } = currentDateRange;
      
      // Ensure end date includes the full day, especially for Sundays
      console.log(`[DATE RANGE CHECK] End day is ${format(end, 'EEEE')}, date: ${format(end, 'yyyy-MM-dd')}`);
      
      // Always fetch schedules across ALL teams for this guard
      const schedules = await fetchAllGuardSchedules(guardId, { start, end });
      console.log(`[TOTAL WORK VIEW] Loaded ${schedules.length} schedules for guard ${guardId}`);
      
      // Log each schedule to debug
      schedules.forEach(schedule => {
        const startDate = fromLocalISOString(schedule.start_date);
        const endDate = fromLocalISOString(schedule.end_date);
        console.log(`[LOADED SHIFT] ${schedule.title}: ${format(startDate, 'yyyy-MM-dd HH:mm')} - ${format(endDate, 'yyyy-MM-dd HH:mm')}`);
      });
      
      // All schedules now have team_name from the service
      
      // Filter schedules based on week visibility for non-admins
      const visibleSchedules = isAdmin ? 
        schedules : 
        schedules.filter(schedule => {
          const scheduleDate = fromLocalISOString(schedule.start_date);
          const weekStart = startOfWeek(scheduleDate, { weekStartsOn: 1 });
          
          // Special handling for Sunday, which belongs to the current week
          if (isSunday(scheduleDate)) {
            console.log(`[SUNDAY CHECK] Found Sunday schedule: ${schedule.id} on ${format(scheduleDate, 'yyyy-MM-dd')}`);
          }
          
          return isDateVisible(weekStart);
        });
      
      console.log(`[TOTAL WORK VIEW] Total visible schedules for guard ${guardId}: ${visibleSchedules.length}`);
      setGuardSchedules(visibleSchedules);
      setAllGuardsSchedules([]);
    } catch (error) {
      console.error('[TOTAL WORK VIEW] Error loading guard schedules:', error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("errorLoadingSchedules"),
      });
    } finally {
      setIsLoadingGuardSchedules(false);
    }
  };

  useEffect(() => {
    if (selectedGuardId) {
      loadGuardSchedules(selectedGuardId);
    } else if (isAdmin) {
      loadAllGuardsSchedules();
    } else if (user) {
      loadGuardSchedules(user.id);
    }
  }, [currentDateRange, selectedGuardId, isAdmin, user]);

  const handleGuardSelect = async (guardId: string) => {
    if (!isAdmin && user && guardId !== user.id && guardId !== 'all') {
      toast({
        variant: "destructive",
        title: t("error") || "Error",
        description: t("permissionDenied") || "You can only view your own schedule",
      });
      return;
    }
    
    if (guardId === 'all' && isAdmin) {
      setSelectedGuardId(null);
      await loadAllGuardsSchedules();
    } else {
      setSelectedGuardId(guardId);
      await loadGuardSchedules(guardId);
    }
  };

  return {
    selectedGuardId,
    guardSchedules,
    allGuardsSchedules,
    isLoadingGuardSchedules,
    handleGuardSelect,
    loadAllGuardsSchedules,
    loadGuardSchedules
  };
};
